import axios from "axios";
import { Constants } from "../../utils/constants";
import {
  CreateBindingRequest,
  BindingView,
  ApiKeyResponse,
  ApiKeyListItem,
  MintApiKeyRequest,
  CredentialsRequest,
  WebhookInfo,
  WebhookRegistrationRequest,
  WebhookRegistrationResponse,
  TestResult,
  StagedBuilding,
  StagedSpace,
  ProviderDescriptor,
  PendingVendorUpdateView,
  BulkResyncResponse,
} from "../../types/federation";

class FederationService {
  private api;

  constructor() {
    this.api = axios.create({
      baseURL: `${Constants.INTEGRATIONS_API_BASE_URL}/api/v2/integrations`,
      headers: { "Content-Type": "application/json" },
    });

    const INTEGRATIONS_API_TZ_OFFSET = "+03:00";
    const naiveIsoTimestamp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/;

    const normalizeServerTimestamps = (value: any): any => {
      if (typeof value === "string") {
        return naiveIsoTimestamp.test(value) ? `${value}${INTEGRATIONS_API_TZ_OFFSET}` : value;
      }
      if (Array.isArray(value)) {
        return value.map(normalizeServerTimestamps);
      }
      if (value !== null && typeof value === "object") {
        return Object.fromEntries(
          Object.entries(value).map(([key, val]) => [key, normalizeServerTimestamps(val)])
        );
      }
      return value;
    };

    const authInterceptor = (config: any) => {
      const token = localStorage.getItem("authToken") || localStorage.getItem("access_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    };
    this.api.interceptors.request.use(authInterceptor);

    this.api.interceptors.response.use(
      (response: any) => ({
        data: normalizeServerTimestamps(response.data),
        status: response.status,
        message: response.data?.message,
      }),
      (error: any) => {
        throw error;
      }
    );
  }

  // Bindings
  async listBindings(direction?: string): Promise<BindingView[]> {
    const params: Record<string, string> = {};
    if (direction) params.direction = direction;
    const res = await this.api.get("/bindings", { params });
    return res.data.data || res.data;
  }

  async getBinding(id: number): Promise<BindingView> {
    const res = await this.api.get(`/bindings/${id}`);
    return res.data.data || res.data;
  }

  async createBinding(payload: CreateBindingRequest): Promise<BindingView> {
    const res = await this.api.post("/bindings", payload);
    return res.data.data || res.data;
  }

  async testBinding(id: number): Promise<TestResult> {
    const res = await this.api.post(`/bindings/${id}/test`);
    return res.data.data || res.data;
  }

  async activateBinding(id: number): Promise<BindingView> {
    const res = await this.api.post(`/bindings/${id}/activate`);
    return res.data.data || res.data;
  }

  async pauseBinding(id: number): Promise<BindingView> {
    const res = await this.api.post(`/bindings/${id}/pause`);
    return res.data.data || res.data;
  }

  async archiveBinding(id: number): Promise<BindingView> {
    const res = await this.api.post(`/bindings/${id}/archive`);
    return res.data.data || res.data;
  }

  async rebackfillBinding(id: number): Promise<BindingView> {
    const res = await this.api.post(`/bindings/${id}/rebackfill`);
    return res.data.data || res.data;
  }

  // API Keys
  async listApiKeys(bindingId: number): Promise<ApiKeyListItem[]> {
    const res = await this.api.get(`/bindings/${bindingId}/apikeys`);
    return res.data.data || res.data;
  }

  async mintApiKey(bindingId: number, payload: MintApiKeyRequest): Promise<ApiKeyResponse> {
    const res = await this.api.post(`/bindings/${bindingId}/apikeys`, payload);
    return res.data.data || res.data;
  }

  async revokeApiKey(bindingId: number, keyId: string): Promise<void> {
    await this.api.delete(`/bindings/${bindingId}/apikeys/${keyId}`);
  }

  async revokeAllApiKeys(bindingId: number): Promise<void> {
    await this.api.delete(`/bindings/${bindingId}/apikeys`);
  }

  // Credentials
  async setCredentials(bindingId: number, payload: CredentialsRequest): Promise<void> {
    await this.api.put(`/bindings/${bindingId}/credentials`, payload);
  }

  // Webhook
  async getWebhookInfo(bindingId: number): Promise<WebhookInfo> {
    const res = await this.api.get(`/bindings/${bindingId}/webhook`);
    return res.data.data || res.data;
  }

  async rotateWebhookSecret(bindingId: number): Promise<void> {
    await this.api.post(`/bindings/${bindingId}/rotate-webhook-secret`);
  }

  async registerWebhookOnHost(
    hostBaseUrl: string,
    hostApiKey: string,
    payload: WebhookRegistrationRequest
  ): Promise<WebhookRegistrationResponse> {
    let targetUrl = hostBaseUrl;
    try {
      const url = new URL(hostBaseUrl);
      if (url.hostname.startsWith("app.")) {
        url.hostname = url.hostname.replace(/^app\./, "api.");
        // Ensure trailing slash is handled correctly if present in pathname
        targetUrl = url.origin + (url.pathname === "/" ? "" : url.pathname);
      }
    } catch (e) {
      // fallback to original hostBaseUrl if URL is not absolute
    }

    const res = await axios.post(`${targetUrl}/api/v2/federation/webhooks/register`, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${hostApiKey}`,
      },
    });
    return res.data.data || res.data;
  }

  // Config
  async setBindingConfig(bindingId: number, config: Record<string, string>): Promise<BindingView> {
    const res = await this.api.put(`/bindings/${bindingId}/config`, { config });
    return res.data.data || res.data;
  }

  // Staging
  async listStagedBuildings(bindingId?: number, size = 100): Promise<StagedBuilding[]> {
    const params: Record<string, any> = { size };
    if (bindingId) params.bindingId = bindingId;
    const res = await this.api.get("/staging/buildings", { params });
    const payload = res.data.data || res.data;
    // Handle paginated response { content: [...] } or plain array
    if (payload && Array.isArray(payload.content)) return payload.content;
    if (Array.isArray(payload)) return payload;
    return [];
  }

  async listStagedSpaces(bindingId?: number, stagedBuildingId?: number, size = 100): Promise<StagedSpace[]> {
    const params: Record<string, any> = { size };
    if (bindingId) params.bindingId = bindingId;
    if (stagedBuildingId) params.stagedBuildingId = stagedBuildingId;
    const res = await this.api.get("/staging/spaces", { params });
    const payload = res.data.data || res.data;
    // Handle paginated response { content: [...] } or plain array
    if (payload && Array.isArray(payload.content)) return payload.content;
    if (Array.isArray(payload)) return payload;
    return [];
  }

  async updateStagedItem(type: "buildings" | "spaces", id: number, payload: Record<string, any>): Promise<any> {
    const res = await this.api.patch(`/staging/${type}/${id}`, payload);
    return res.data.data || res.data;
  }

  async approveStagedItem(type: "buildings" | "spaces", id: number, extraPayload?: Record<string, any>): Promise<void> {
    await this.api.post(`/staging/${type}/${id}/approve`, extraPayload || {});
  }

  async rejectStagedItem(type: "buildings" | "spaces", id: number): Promise<void> {
    await this.api.post(`/staging/${type}/${id}/reject`);
  }

  async resyncStagedBuilding(id: number): Promise<void> {
    await this.api.post(`/staging/resync/building/${id}`);
  }

  async resyncStagedSpace(id: number): Promise<void> {
    await this.api.post(`/staging/resync/space/${id}`);
  }

  /**
   * Vendor changes awaiting review for the caller's org.
   *
   * <p>A promoted space is never silently updated by a vendor delta; the change is recorded and
   * applied on request. This is how the operator sees what is waiting.
   */
  async listPendingUpdates(): Promise<PendingVendorUpdateView[]> {
    const res = await this.api.get("/staging/pending");
    return res.data.data || res.data;
  }

  /** Apply several pending updates in one decision. Partial success is reported per entity. */
  async resyncMany(spaceIds: number[], buildingIds: number[]): Promise<BulkResyncResponse> {
    const res = await this.api.post("/staging/resync", { spaceIds, buildingIds });
    return res.data.data || res.data;
  }

  /**
   * Wait for a re-backfill to finish.
   *
   * <p>Polls until the binding leaves ACTIVATING. Deliberately not "until ACTIVE": a backfill can
   * legitimately end in ERROR when the vendor is unhealthy, and waiting for ACTIVE would hang
   * forever on a binding that already gave up. Returns the terminal binding either way so the caller
   * can tell the two apart.
   */
  async waitForBackfill(id: number, timeoutMs = 120000, intervalMs = 3000): Promise<BindingView> {
    const deadline = Date.now() + timeoutMs;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const binding = await this.getBinding(id);
      if (binding.status !== "ACTIVATING") return binding;
      if (Date.now() >= deadline) return binding;
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
  }

  // Provider descriptor methods
  async getProviders(): Promise<ProviderDescriptor[]> {
    const res = await this.api.get("/providers");
    return res.data.data || res.data;
  }
}

export const federationService = new FederationService();
export default federationService;
